// Voicetrack-Studio: Produktion eines VT-Slots mit großem Mikro-Knopf (Einzähler, Pegel, Zeitring),
// Stundenuhr mit allen Positionen, Senderwahl im Verbund und drei Wellenform-Spuren
// (Titel davor · Sprache · Titel danach) zum Platzieren, Ducken und Schneiden. Eigene Gestaltung.

import { useEffect, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad } from "../../api";
import { LaneEditor, laneStarts, usePreview, useTransition } from "../../wave";

const fmtWhen = (iso: string) => new Date(iso).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function beep(ctx: AudioContext, at: number, hi = false) {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.value = hi ? 1320 : 880;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(0.3, at + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.15);
  o.connect(g).connect(ctx.destination);
  o.start(at);
  o.stop(at + 0.2);
}

/** Hour clock: positions of the hour's elements, VT slots highlighted, click to jump. */
function HourClock({ entries, current, onPick }: { entries: any[]; current: string; onPick: (id: string) => void }) {
  const r = 78, c = 90;
  const pos = (iso: string) => {
    const d = new Date(iso);
    const a = ((d.getMinutes() * 60 + d.getSeconds()) / 3600) * 2 * Math.PI - Math.PI / 2;
    return [c + Math.cos(a) * r, c + Math.sin(a) * r];
  };
  return (
    <svg width={180} height={180}>
      <circle cx={c} cy={c} r={r + 8} fill="#14211c" stroke="#2c3f37" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
        return <line key={i} x1={c + Math.cos(a) * (r + 2)} y1={c + Math.sin(a) * (r + 2)} x2={c + Math.cos(a) * (r + 7)} y2={c + Math.sin(a) * (r + 7)} stroke="#4a625a" strokeWidth={i % 3 ? 1 : 2} />;
      })}
      {entries.map((e) => {
        const [x, y] = pos(e.planned_start);
        const vt = e.type === "voicetrack";
        return (
          <circle key={e.id} cx={x} cy={y} r={vt ? (e.id === current ? 8 : 6) : 3} fill={vt ? (e.status === "unfilled" ? "#e5534b" : "#3fb37f") : "#5e7a70"}
            stroke={e.id === current ? "#fff" : "none"} strokeWidth={2} style={{ cursor: vt ? "pointer" : "default" }} onClick={() => vt && onPick(e.id)}>
            <title>{`${new Date(e.planned_start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} ${e.title || e.label || e.type}`}</title>
          </circle>
        );
      })}
      <text x={c} y={c + 6} textAnchor="middle" fontSize={20} fill="#e6efe9" fontWeight={700}>{entries[0] ? new Date(entries[0].hour_start).getHours() : ""}</text>
    </svg>
  );
}

export function VtStudio({ slots, hourEntries, startId, onChanged }: { slots: any[]; hourEntries: any[]; startId?: string; onChanged: () => void }) {
  const [id, setId] = useState<string>(startId ?? slots[0]?.id);
  const idx = slots.findIndex((s) => s.id === id);
  const slot = slots[idx] ?? slots[0];
  const t = useTransition(slot?.id ?? null);
  const { play, stop: stopPreview, playhead } = usePreview(t.lanes);
  const { data: stations } = useLoad<any[]>("/api/stations");
  const [vtStations, setVtStations] = useState<string[]>([]);
  const [maxS, setMaxS] = useState(180);
  const [countIn, setCountIn] = useState(true);
  const [phase, setPhase] = useState<"idle" | "count" | "rec" | "busy">("idle");
  const [count, setCount] = useState(0);
  const [secs, setSecs] = useState(0);
  const [level, setLevel] = useState(-90);
  const [micOn, setMicOn] = useState(false);
  const [drop, setDrop] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const cleanup = useRef<() => void>(() => {});

  useEffect(() => { setVtStations(slot?.vt_stations ?? []); }, [slot?.id]);
  useEffect(() => () => cleanup.current(), []);

  const upload = async (data: Blob) => {
    setPhase("busy");
    try {
      const q = vtStations.length ? `&stations=${encodeURIComponent(vtStations.join(","))}` : "";
      const r = await fetch(`/api/vt/slots/${slot.id}/audio?over_intro=0${q}`, { method: "POST", body: data, headers: { "content-type": "application/x-radiocast-vt" }, credentials: "same-origin" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error ?? `Fehler ${r.status}`);
      const shared = (d.shared ?? []) as any[];
      toast(`Gespeichert (${fmtMs(d.length_ms)})${shared.length ? ` · auf ${shared.filter((x) => x.ok).length} von ${shared.length} weiteren Sendern` : ""}`);
      for (const s of shared.filter((x) => !x.ok)) toast(`${s.station}: ${s.why}`, true);
      await t.reload();
      onChanged();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setPhase("idle");
    }
  };

  const record = async () => {
    if (phase === "rec") { recRef.current?.stop(); return; }
    if (phase !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      setMicOn(true);
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 2048;
      src.connect(an);
      const buf = new Float32Array(an.fftSize);
      let raf = 0;
      const loop = () => {
        an.getFloatTimeDomainData(buf);
        let peak = 0;
        for (const x of buf) peak = Math.max(peak, Math.abs(x));
        setLevel(peak > 1e-5 ? 20 * Math.log10(peak) : -90);
        raf = requestAnimationFrame(loop);
      };
      loop();
      if (countIn) {
        setPhase("count");
        for (let i = 3; i >= 1; i--) {
          setCount(i);
          beep(ctx, ctx.currentTime + 0.01, i === 1);
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 256_000 } : undefined);
      const chunks: Blob[] = [];
      rec.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      const t0 = Date.now();
      const timer = window.setInterval(() => {
        const s = (Date.now() - t0) / 1000;
        setSecs(s);
        if (s >= maxS) rec.stop();
      }, 100);
      cleanup.current = () => { window.clearInterval(timer); cancelAnimationFrame(raf); stream.getTracks().forEach((x) => x.stop()); ctx.close().catch(() => {}); setMicOn(false); };
      rec.onstop = () => {
        cleanup.current();
        void upload(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
      };
      recRef.current = rec;
      rec.start(250);
      setSecs(0);
      setPhase("rec");
    } catch {
      setPhase("idle");
      toast("Kein Zugriff auf das Mikrofon – bitte im Browser erlauben", true);
    }
  };

  /** Music under the voice: previous title lowered from where the voice starts, next title starts low and comes up. */
  const duck = (db = -12) => {
    const l = t.lanes;
    if (!l?.cur.media) return;
    const st = laneStarts(l);
    const next = l.next;
    const prev = l.prev;
    const vtLen = (l.cur.marks.cue_out ?? l.cur.media.duration_ms) - (l.cur.marks.cue_in ?? 0);
    const upd: any = { ...l };
    if (prev?.media) {
      const pm = prev.marks;
      const seg = pm.start_next ?? pm.cue_out;
      upd.prev = { ...prev, marks: { ...pm, level_points: [{ ms: Math.max(pm.cue_in ?? 0, seg - 400), db: 0 }, { ms: seg + 300, db }, { ms: pm.cue_out ?? prev.media.duration_ms, db: db - 20 }] } };
    }
    if (next?.media) {
      const nm = next.marks;
      const ci = nm.cue_in ?? 0;
      const overlap = Math.max(0, (st.cur ?? 0) + vtLen - (st.next ?? 0));
      upd.next = { ...next, marks: { ...nm, level_points: overlap > 300 ? [{ ms: ci, db }, { ms: ci + overlap, db }, { ms: ci + overlap + 800, db: 0 }] : [] } };
    }
    t.setLanes(upd);
  };
  const remove = async () => {
    if (!window.confirm("Aufnahme aus diesem Slot entfernen?")) return;
    await api(`/api/vt/slots/${slot.id}/audio`, { method: "DELETE" });
    await t.reload();
    onChanged();
  };
  const saveStations = async (list: string[]) => {
    setVtStations(list);
    if (!slot.media_id && !t.lanes?.cur.media) return;
    const r = await api(`/api/vt/slots/${slot.id}/stations`, { method: "PUT", body: { stations: list } });
    for (const s of r.shared ?? []) toast(s.ok ? `Läuft auch auf ${stations?.find((x) => x.id === s.station)?.name ?? s.station}` : `${s.station}: ${s.why}`, !s.ok);
  };

  if (!slot) return null;
  const others = (stations ?? []).filter((s) => s.id !== (document.cookie.match(/rc_station=([^;]+)/)?.[1] ?? "main"));
  const ring = phase === "rec" ? Math.min(1, secs / maxS) : 0;
  const lvl = Math.max(0, Math.min(1, (level + 60) / 60));
  const st = t.lanes ? laneStarts(t.lanes) : {};

  return (
    <div style={{ background: "#0e1613", color: "#e6efe9", borderRadius: 14, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}>
      <div className="row wrap" style={{ background: "linear-gradient(90deg,#1f8a5b,#2aa36d)", padding: "10px 16px", gap: 10 }}>
        <b>Voicetrack-Produktion · {fmtWhen(slot.planned_start)} · Position {idx + 1} von {slots.length}</b>
        <span className="spacer" />
        <span className="small" style={{ background: "rgba(0,0,0,.2)", padding: "2px 8px", borderRadius: 10 }}>{micOn ? "● Mikro aktiv" : "Mikro aus"}</span>
        <button className="btn small" disabled={idx <= 0} onClick={() => setId(slots[idx - 1].id)}>← Slot davor</button>
        <button className="btn small" disabled={idx >= slots.length - 1} onClick={() => setId(slots[idx + 1].id)}>Nächster Slot →</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "250px 200px 1fr", gap: 16, padding: 16 }}>
        {/* Mic */}
        <div className="stack" style={{ alignItems: "center" }}>
          <div style={{ position: "relative", width: 170, height: 170 }}>
            <svg width={170} height={170} style={{ position: "absolute", inset: 0 }}>
              <circle cx={85} cy={85} r={78} fill="none" stroke="#24352e" strokeWidth={8} />
              <circle cx={85} cy={85} r={78} fill="none" stroke={ring > 0.9 ? "#e5534b" : "#3fb37f"} strokeWidth={8} strokeDasharray={`${ring * 490} 490`} transform="rotate(-90 85 85)" strokeLinecap="round" />
            </svg>
            <button onClick={record} disabled={phase === "busy" || phase === "count"} title={phase === "rec" ? "Aufnahme beenden" : "Aufnahme starten"}
              style={{ position: "absolute", inset: 18, borderRadius: "50%", border: 0, cursor: "pointer", fontSize: phase === "count" ? 48 : 30, fontWeight: 700, color: "#fff",
                background: phase === "rec" ? "radial-gradient(circle,#ff6b5f,#c8382f)" : "radial-gradient(circle,#3a4f47,#1b2823)", boxShadow: phase === "rec" ? "0 0 30px rgba(229,83,75,.6)" : "inset 0 0 0 2px #3fb37f" }}>
              {phase === "count" ? count : phase === "rec" ? "■" : phase === "busy" ? "…" : "🎙"}
            </button>
          </div>
          <div style={{ fontSize: 26, fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{fmtMs(secs * 1000)} <span className="small" style={{ opacity: 0.6 }}>/ {fmtMs(maxS * 1000)}</span></div>
          <div style={{ width: "100%", height: 10, borderRadius: 5, background: "#24352e", overflow: "hidden" }}>
            <div style={{ width: `${lvl * 100}%`, height: "100%", background: "linear-gradient(90deg,#3fb37f 0%,#3fb37f 65%,#e9c14a 82%,#e5534b 100%)", backgroundSize: `${100 / Math.max(lvl, 0.01)}% 100%` }} />
          </div>
          <div className="row small" style={{ gap: 8 }}>
            <label className="row"><input type="checkbox" checked={countIn} onChange={(e) => setCountIn(e.target.checked)} /> Einzähler</label>
            <select value={maxS} onChange={(e) => setMaxS(Number(e.target.value))} style={{ width: 90 }}>{[30, 60, 90, 120, 180, 300].map((s) => <option key={s} value={s}>max. {fmtMs(s * 1000)}</option>)}</select>
          </div>
          <div onDragOver={(e) => { e.preventDefault(); setDrop(true); }} onDragLeave={() => setDrop(false)}
            onDrop={(e) => { e.preventDefault(); setDrop(false); const f = e.dataTransfer.files?.[0]; if (f) void upload(f); }}
            style={{ width: "100%", border: `2px dashed ${drop ? "#3fb37f" : "#34483f"}`, borderRadius: 10, padding: 12, textAlign: "center", fontSize: 12, opacity: 0.85 }}>
            Datei hierher ziehen (z. B. vom RodeCaster)
            <div><label className="btn small" style={{ marginTop: 6, cursor: "pointer" }}>Datei wählen<input type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} /></label></div>
          </div>
          {t.lanes?.cur.media && <button className="btn small danger" onClick={remove}>Aufnahme entfernen</button>}
        </div>
        {/* Hour */}
        <div className="stack" style={{ alignItems: "center" }}>
          <HourClock entries={hourEntries} current={slot.id} onPick={setId} />
          <div style={{ width: "100%", maxHeight: 220, overflowY: "auto", fontSize: 12 }}>
            {hourEntries.map((e) => (
              <div key={e.id} onClick={() => e.type === "voicetrack" && setId(e.id)} style={{ padding: "3px 6px", borderRadius: 6, cursor: e.type === "voicetrack" ? "pointer" : "default", background: e.id === slot.id ? "#22382f" : undefined, color: e.type === "voicetrack" ? (e.status === "unfilled" ? "#ff8b84" : "#7fdcab") : "#b9c9c1" }}>
                {new Date(e.planned_start).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} {e.type === "voicetrack" ? "🎙 Voicetrack" : `${e.artist ? `${e.artist} – ` : ""}${e.title || e.label}`}
              </div>
            ))}
          </div>
        </div>
        {/* Lanes */}
        <div style={{ minWidth: 0 }}>
          {others.length > 0 && (
            <div className="row wrap" style={{ gap: 6, marginBottom: 8 }}>
              <span className="small" style={{ opacity: 0.7 }}>Läuft auch auf:</span>
              {others.map((s) => {
                const on = vtStations.includes(s.id);
                return <button key={s.id} className="btn small" onClick={() => saveStations(on ? vtStations.filter((x) => x !== s.id) : [...vtStations, s.id])}
                  style={{ background: on ? s.color : "transparent", color: on ? "#fff" : "#cfe0d8", borderColor: s.color }}>{on ? "✓ " : ""}{s.name}</button>;
              })}
              <button className="btn small" onClick={() => saveStations(others.map((s) => s.id))}>alle</button>
              <button className="btn small" onClick={() => saveStations([])}>nur dieser</button>
            </div>
          )}
          {!t.lanes ? <div>Lade …</div> : <LaneEditor lanes={t.lanes} onChange={t.setLanes} vtSlot playhead={playhead} />}
          <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
            <button className="btn small" onClick={() => play(Math.max(0, (st.cur ?? 0) - 6000), 30)}>▶ Ab Ende davor</button>
            {t.lanes?.next && <button className="btn small" onClick={() => play(Math.max(0, (st.next ?? 0) - 6000), 14)}>▶ Übergang in den nächsten</button>}
            {playhead != null && <button className="btn small" onClick={stopPreview}>■ Stopp</button>}
            <button className="btn small" disabled={!t.lanes?.cur.media} onClick={() => duck(-12)} title="Musik unter der Sprache absenken">Musik unter Sprache ducken</button>
            <button className="btn small" disabled={!t.canUndo} onClick={t.undo}>↶</button>
            <button className="btn small" onClick={t.reset}>Zurücksetzen</button>
            <span className="spacer" />
            <button className="btn small primary" disabled={!t.dirty} onClick={async () => { await t.save(); onChanged(); }}>Platzierung speichern</button>
          </div>
          <p className="small" style={{ opacity: 0.6, marginBottom: 0 }}>Sprachblock nach links ziehen = startet früher über dem Ausklang · nächsten Titel nach links ziehen = startet schon unter der Sprache · Ränder der Sprachspur = schneiden.</p>
        </div>
      </div>
    </div>
  );
}
